import * as THREE from 'three'
import Experience from '../Experience.js'

const finishes = {
    oak: {
        name: 'Natural Oak',
        color: '#c79254',
        accent: '#ead0a6'
    },
    walnut: {
        name: 'Walnut',
        color: '#5b3826',
        accent: '#9b6b47'
    },
    ash: {
        name: 'White Ash',
        color: '#e8e3d6',
        accent: '#b8aa92'
    },
    graphite: {
        name: 'Graphite',
        color: '#303238',
        accent: '#777b84'
    }
}

const defaultSettings = {
    width: 240,
    height: 220,
    depth: 60,
    bays: 3,
    shelves: 3,
    drawers: 2,
    rods: true,
    doors: 'open',
    finish: 'oak'
}

const maxBayWidth = 80
const maxShelfGap = 0.5

export default class Wardrobe
{
    constructor()
    {
        this.experience = new Experience()
        this.scene = this.experience.scene
        this.settings = { ...defaultSettings, ...window.wardrobeSettings }
        this.group = new THREE.Group()
        this.group.name = 'Wardrobe'
        this.scene.add(this.group)

        this.materials = {}
        this.setMaterials()
        this.build()

        this.handleUpdate = (event) =>
        {
            this.settings = { ...this.settings, ...event.detail }
            this.clearGroup()
            this.disposeMaterials()
            this.setMaterials()
            this.build()
        }

        window.addEventListener('wardrobe:update', this.handleUpdate)
    }

    setMaterials()
    {
        const finish = finishes[this.settings.finish] || finishes.oak
        const environmentMap = this.experience.resources.items.environmentMapTexture

        this.materials.board = new THREE.MeshStandardMaterial({
            color: finish.color,
            roughness: 0.58,
            metalness: 0.02
        })

        this.materials.inner = new THREE.MeshStandardMaterial({
            color: finish.accent,
            roughness: 0.64,
            metalness: 0.01
        })

        this.materials.back = new THREE.MeshStandardMaterial({
            color: finish.color,
            roughness: 0.62,
            metalness: 0.01
        })

        this.materials.handle = new THREE.MeshStandardMaterial({
            color: '#1f2328',
            roughness: 0.28,
            metalness: 0.7
        })

        const isGlass = this.settings.doors === 'glass'

        this.materials.door = new THREE.MeshPhysicalMaterial({
            color: finish.color,
            roughness: isGlass ? 0.04 : 0.5,
            metalness: isGlass ? 0.05 : 0.02,
            transparent: isGlass,
            opacity: isGlass ? 0.36 : 1,
            transmission: isGlass ? 0.45 : 0,
            thickness: isGlass ? 0.035 : 0,
            clearcoat: isGlass ? 1 : 0,
            clearcoatRoughness: isGlass ? 0.03 : 0,
            ior: isGlass ? 1.45 : 1.5,
            envMap: environmentMap,
            envMapIntensity: isGlass ? 2.4 : 0.35
        })
    }

    build()
    {
        this.clearGroup()

        const width = this.settings.width / 100
        const height = this.settings.height / 100
        const depth = this.settings.depth / 100
        const bayCount = Math.max(1, Math.ceil(this.settings.width / maxBayWidth))
        const board = 0.065
        const back = 0.035
        const drawerHeight = 0.22
        const interiorWidth = width - board * 2
        const bayWidth = interiorWidth / bayCount
        const drawerCount = Math.min(this.settings.drawers, 3)
        const drawerStackHeight = drawerCount * drawerHeight

        this.group.position.set(-width * 0.5, 0, 0)

        this.addBox('left side', board, height, depth, board * 0.5, height * 0.5, 0, this.materials.board)
        this.addBox('right side', board, height, depth, width - board * 0.5, height * 0.5, 0, this.materials.board)
        this.addBox('top', width, board, depth, width * 0.5, height - board * 0.5, 0, this.materials.board)
        this.addBox('bottom', width, board, depth, width * 0.5, board * 0.5, 0, this.materials.board)
        this.addBox('back panel', width, height, back, width * 0.5, height * 0.5, -depth * 0.5 + back * 0.5, this.materials.back)
        this.addFrontFrame(width, height, depth, board)

        for(let index = 1; index < bayCount; index++)
        {
            const x = board + bayWidth * index
            this.addBox('divider', board, height - board * 2, depth * 0.96, x, height * 0.5, 0.01, this.materials.board)
            this.addBox('front divider face', board * 1.08, height - board * 2, board * 0.42, x, height * 0.5, depth * 0.5 + board * 0.16, this.materials.board)
        }

        for(let bay = 0; bay < bayCount; bay++)
        {
            const xCenter = board + bayWidth * bay + bayWidth * 0.5
            this.addBayInterior(xCenter, bayWidth, height, depth, board, drawerHeight, drawerCount)
        }

        if(drawerCount > 0)
            this.addDrawerDoorPartitions(width, depth, board, bayCount, bayWidth, drawerStackHeight)

        if(this.settings.doors !== 'open')
            this.addDoors(width, height, depth, board, bayCount, drawerStackHeight)

        this.addOutline()
        this.addShadowPlane(width, depth)
    }

    addBayInterior(xCenter, bayWidth, height, depth, board, drawerHeight, drawerCount)
    {
        const shelfWidth = bayWidth - board * 0.45
        const shelfDepth = depth * 0.88
        const drawers = drawerCount
        const drawerStackHeight = drawers * drawerHeight
        const lowerClearance = drawerStackHeight > 0 ? drawerStackHeight + board * 2.25 : 0
        const doorBottom = board + lowerClearance
        const doorTop = height - board
        const doorHeight = Math.max(maxShelfGap, doorTop - doorBottom)
        const compartmentCount = Math.max(1, Math.ceil(doorHeight / maxShelfGap))
        const shelfCount = Math.max(0, compartmentCount - 1)

        for(let shelf = 0; shelf < shelfCount; shelf++)
        {
            const y = doorBottom + doorHeight * ((shelf + 1) / compartmentCount)
            this.addBox('shelf', shelfWidth, board * 0.75, shelfDepth, xCenter, y, 0.01, this.materials.inner)
        }

        for(let drawer = 0; drawer < drawers; drawer++)
        {
            const y = board + drawerHeight * drawer + drawerHeight * 0.5
            this.addDrawerBox(xCenter, y, shelfWidth, drawerHeight, depth, board)
            this.addBox('drawer pull', shelfWidth * 0.35, 0.018, 0.025, xCenter, y, depth * 0.5 + board * 1.1, this.materials.handle)
        }

        if(drawers > 0)
            this.addDrawerBankFrame(xCenter, shelfWidth, drawers, drawerHeight, depth, board)

        if(this.settings.rods)
        {
            const y = Math.max(height - 0.55, board + drawerStackHeight + 0.58)
            const rod = new THREE.Mesh(
                new THREE.CylinderGeometry(0.018, 0.018, shelfWidth * 0.8, 24),
                this.materials.handle
            )
            rod.name = 'hanging rod'
            rod.rotation.z = Math.PI * 0.5
            rod.position.set(xCenter, y, -depth * 0.03)
            rod.castShadow = true
            this.group.add(rod)
        }
    }

    addFrontFrame(width, height, depth, board)
    {
        const z = depth * 0.5 + board * 0.16
        const frameDepth = board * 0.42

        this.addBox('left front stile', board * 1.08, height, frameDepth, board * 0.5, height * 0.5, z, this.materials.board)
        this.addBox('right front stile', board * 1.08, height, frameDepth, width - board * 0.5, height * 0.5, z, this.materials.board)
        this.addBox('top front rail', width, board * 1.08, frameDepth, width * 0.5, height - board * 0.5, z, this.materials.board)
        this.addBox('bottom front rail', width, board * 1.08, frameDepth, width * 0.5, board * 0.5, z, this.materials.board)
    }

    addDrawerBox(xCenter, yCenter, shelfWidth, drawerHeight, depth, board)
    {
        const drawerWidth = shelfWidth - board * 0.42
        const drawerDepth = depth * 0.64
        const drawerWall = board * 0.42
        const drawerBottom = board * 0.34
        const drawerHeightInner = drawerHeight * 0.64
        const frontZ = depth * 0.5 + 0.025
        const boxCenterZ = frontZ - drawerDepth * 0.5 - board * 0.2
        const sideOffsetX = drawerWidth * 0.5 - drawerWall * 0.5
        const bottomY = yCenter - drawerHeightInner * 0.5

        this.addBox('drawer box bottom', drawerWidth, drawerBottom, drawerDepth, xCenter, bottomY, boxCenterZ, this.materials.inner)
        this.addBox('drawer box left side', drawerWall, drawerHeightInner, drawerDepth, xCenter - sideOffsetX, yCenter, boxCenterZ, this.materials.inner)
        this.addBox('drawer box right side', drawerWall, drawerHeightInner, drawerDepth, xCenter + sideOffsetX, yCenter, boxCenterZ, this.materials.inner)
        this.addBox('drawer box back', drawerWidth, drawerHeightInner, drawerWall, xCenter, yCenter, boxCenterZ - drawerDepth * 0.5 + drawerWall * 0.5, this.materials.inner)
        this.addBox('drawer front', drawerWidth, drawerHeight * 0.82, board * 1.25, xCenter, yCenter, frontZ, this.materials.inner)
    }

    addDrawerBankFrame(xCenter, shelfWidth, drawers, drawerHeight, depth, board)
    {
        const frameWidth = shelfWidth + board * 0.18
        const frameHeight = drawers * drawerHeight + board * 0.38
        const frameDepth = board * 0.55
        const frameZ = depth * 0.5 + board * 0.16
        const centerY = board + drawers * drawerHeight * 0.5
        const sideX = frameWidth * 0.5 - board * 0.24

        this.addBox('drawer bank left frame', board * 0.48, frameHeight, frameDepth, xCenter - sideX, centerY, frameZ, this.materials.board)
        this.addBox('drawer bank right frame', board * 0.48, frameHeight, frameDepth, xCenter + sideX, centerY, frameZ, this.materials.board)
        this.addBox('drawer bank bottom rail', frameWidth, board * 0.42, frameDepth, xCenter, board + board * 0.08, frameZ, this.materials.board)
        this.addBox('drawer bank top rail', frameWidth, board * 0.42, frameDepth, xCenter, board + drawers * drawerHeight + board * 0.08, frameZ, this.materials.board)

        for(let index = 1; index < drawers; index++)
        {
            const y = board + drawerHeight * index
            this.addBox('drawer row separator rail', frameWidth, board * 0.36, frameDepth, xCenter, y, frameZ, this.materials.board)
        }
    }

    addDrawerDoorPartitions(width, depth, board, bayCount, bayWidth, drawerStackHeight)
    {
        const shelfY = board + drawerStackHeight + board * 0.95
        const shelfDepth = depth + board * 0.18
        const shelfCenterZ = board * 0.08
        const lipZ = depth * 0.5 + board * 0.42
        const shelfThickness = board * 1.15
        const shelfWidth = bayWidth - board * 0.25

        for(let bay = 0; bay < bayCount; bay++)
        {
            const xCenter = board + bayWidth * bay + bayWidth * 0.5
            this.addBox('drawer door partition shelf', shelfWidth, shelfThickness, shelfDepth, xCenter, shelfY, shelfCenterZ, this.materials.board)
            this.addBox('drawer door partition front lip', shelfWidth, shelfThickness * 1.08, board * 0.9, xCenter, shelfY, lipZ, this.materials.board)
        }
    }

    addDoors(width, height, depth, board, bayCount, drawerStackHeight)
    {
        const doorGap = 0.018
        const bayWidth = (width - board * 2) / bayCount
        const doorWidth = bayWidth - doorGap
        const lowerClearance = drawerStackHeight > 0 ? drawerStackHeight + board * 2.25 : 0
        const doorBottom = board + lowerClearance
        const doorHeight = Math.max(0.24, height - doorBottom - board - doorGap)
        const doorCenterY = doorBottom + doorHeight * 0.5

        for(let bay = 0; bay < bayCount; bay++)
        {
            const x = board + bayWidth * bay + bayWidth * 0.5
            const y = doorCenterY
            const z = depth * 0.5 + 0.025
            const door = this.addBox('door', doorWidth, doorHeight, 0.035, x, y, z, this.materials.door)

            if(this.settings.doors === 'hinged')
            {
                const direction = bay % 2 === 0 ? -1 : 1
                door.rotation.y = direction * 0.16
            }

            const handleSide = bay % 2 === 0 ? 1 : -1
            this.addBox('door handle', 0.025, doorHeight * 0.35, 0.03, x + handleSide * doorWidth * 0.36, y, z + 0.045, this.materials.handle)
        }
    }

    addOutline()
    {
        const meshes = []

        this.group.traverse((child) =>
        {
            if(child instanceof THREE.Mesh && child.geometry)
                meshes.push(child)
        })

        meshes.forEach((child) =>
        {
            const edges = new THREE.LineSegments(
                new THREE.EdgesGeometry(child.geometry),
                new THREE.LineBasicMaterial({ color: '#15171a', transparent: true, opacity: 0.18 })
            )
            edges.position.copy(child.position)
            edges.rotation.copy(child.rotation)
            edges.scale.copy(child.scale)
            this.group.add(edges)
        })
    }

    disposeMaterials()
    {
        for(const key in this.materials)
        {
            if(this.materials[key])
            {
                this.materials[key].dispose()
            }
        }
    }

    addShadowPlane(width, depth)
    {
        const shadow = new THREE.Mesh(
            new THREE.PlaneGeometry(width * 1.04, depth * 1.18),
            new THREE.ShadowMaterial({ opacity: 0.16 })
        )
        shadow.name = 'wardrobe contact shadow'
        shadow.rotation.x = -Math.PI * 0.5
        shadow.position.set(width * 0.5, 0.002, 0)
        shadow.receiveShadow = true
        this.group.add(shadow)
    }

    addBox(name, width, height, depth, x, y, z, material)
    {
        const mesh = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), material)
        mesh.name = name
        mesh.position.set(x, y, z)
        mesh.castShadow = true
        mesh.receiveShadow = true
        this.group.add(mesh)
        return mesh
    }

    clearGroup()
    {
        while(this.group.children.length)
        {
            const child = this.group.children.pop()
            child.traverse((item) =>
            {
                if(item.geometry)
                    item.geometry.dispose()
            })
        }
    }

    update()
    {
    }
}
